import { BEIGE_500, BLUE_300, GRAY_200, GRAY_700, GRAY_900 } from 'constants/styling/theme'
import React, { useMemo } from 'react'

import { BorderBoxWrapper } from '../BorderBoxWrapper'
import Typography from '@mui/material/Typography'

/** @interface Props for the TabButton component. */
export interface Props {
  /** The label to be displayed on the button. */
  label: string
  /** Whether the button is active. */
  isActive?: boolean
  /** Whether the button is disabled. */
  disabled?: boolean
  /** The icon to display before the label. */
  icon?: React.ReactNode
  /** Onclick action to be called. */
  onClick?: () => void
  /** Renders a `<button type="button" role="tab" aria-selected={isActive}>` for a tab list; without it, today's plain box */
  isTab?: boolean
  /** Id of the tab, for its panel's `aria-labelledby`. Only with `isTab` */
  id?: string
  /** Id of the panel the tab controls. Only with `isTab` */
  ariaControls?: string
  /** Roving tab index: 0 on the selected tab, -1 on the others. Only with `isTab` */
  tabIndex?: number
  /** Arrow-key navigation, handled by the tab list. Only with `isTab` */
  onKeyDown?: (e: React.KeyboardEvent) => void
}

/**
 * @component
 * `TabButton` renders a button with customizable label, active state, and disabled state.
 * 
 * @example
 *  <TabButton label="Tab 1" isActive={true} onClick={() => console.log('Tab 1 clicked')} />
 */
export const TabButton: React.FC<Props> = ({
  label,
  isActive = false,
  disabled = false,
  icon,
  onClick,
  isTab = false,
  id,
  ariaControls,
  tabIndex,
  onKeyDown,
}) => {

  const backgroundColor = useMemo(() => {
    if (disabled) return GRAY_200
    return isActive ? BEIGE_500 : 'white'
  }, [disabled, isActive])

  return (
    <BorderBoxWrapper
      elevation="none"
      borderRadius="0.4rem"
      borderColor={'transparent'}
      onClick={e => onClick?.()}
      {...(isTab && {
        component: 'button',
        type: 'button',
        role: 'tab',
        'aria-selected': isActive,
        'aria-controls': ariaControls,
        id,
        tabIndex,
        disabled,
        onKeyDown,
      })}
      sx={{
        gap: '0.8rem',
        display: 'flex',
        height: '3.6rem',
        cursor: !disabled ? 'pointer' : 'not-allowed',
        position: 'relative',
        alignItems: 'center',
        padding: '0.8rem 1.6rem 0.8rem 1.2rem',
        backgroundColor,
        '&:hover': {
          backgroundColor: !disabled ? BEIGE_500 : GRAY_200
        },
        ...(isTab && {
          font: 'inherit',
          margin: 0,
          '&:focus-visible': { outline: 'none', boxShadow: `0 0 0 2px ${BLUE_300}` },
        }),
      }}
    >

      {icon && icon}

      <Typography
        variant='text-sm'
        fontWeight={isActive ? 600 : 500}
        color={isActive ? GRAY_900 : GRAY_700}
      >
        {label}
      </Typography>

    </BorderBoxWrapper>
  )
}
